/*
 * Created Date: Thursday October 27th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { FC } from "react";
import "@fontsource/roboto";

import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { SxProps, styled } from "@mui/material";

export interface SwitchCustomInterface {
  label?: string;
  disabled: boolean;
  onChange: (event: any) => void;
  checked: boolean;
  size?: "small" | "medium";
  sx?: SxProps;
}

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
  },
  "& .Mui-checked > .MuiSwitch-thumb": {
    color: "#0D98F2",
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="M19.3,12.7V5c0-0.3-0.2-0.6-0.5-0.7l-7.9-3.8c-0.2-0.2-0.4-0.2-0.6-0.2s-0.4,0-0.6,0.1L5.2,2.7C4.9,2.8,4.7,3.1,4.7,3.4v7.7l0,0v7.8c0,0.3,0.2,0.6,0.5,0.8l7.9,3.8c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2l4.5-2.2c0.3-0.2,0.5-0.4,0.5-0.8L19.3,12.7L19.3,12.7z M7.8,11.2l4.9-2.3v4.8L7.8,11.2z M7.8,13.2l3.9,1.9l-4.9,2.3v-4.8L7.8,13.2z M17.3,12.4l-2.6,1.3V7.9l2.6-1.3V12.4zM9.2,2.7l1.1-0.5l6,2.9l-1.5,0.8l-1.1,0.5l-6-2.9L9.2,2.7z M11.6,7.4L6.7,9.7V4.9L11.6,7.4z M7.8,18.9l4.9-2.3v4.8L7.8,18.9zM14.7,21.4v-5.8l2.2-1.1l0.4-0.2v5.8L14.7,21.4z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    color: "#0D98F2",
    width: 32,
    height: 32,
    "&:before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        "#fff"
      )}" d="M20.5,6.6l-7.9-4.4C12.4,2.1,12.2,2,12,2s-0.4,0.1-0.6,0.2L3.5,6.6C3.2,6.8,3,7.1,3,7.5v9c0,0.4,0.2,0.7,0.5,0.9l7.9,4.4c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2l7.9-4.4c0.3-0.2,0.5-0.5,0.5-0.9v-9C21,7.1,20.8,6.8,20.5,6.6z M11,19.3l-6-3.4V9.2l6,3.4V19.3z M12,10.8L6,7.5l2-1.2l0,0l2.8-1.6l0,0L12,4.2l6,3.3l-1.5,0.9l0,0l0,0L16,8.6l0,0l-3,1.7L12,10.8z M19,15.9l-6,3.4v-6.7l6-3.4V15.9z"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    opacity: "1",
    borderRadius: 20 / 2,
  },
}));

const SwitchCustom: FC<SwitchCustomInterface> = ({
  label,
  disabled,
  onChange,
  checked,
  size = "medium",
  sx,
}) => {
  return (
    <FormGroup
      sx={{
        ...sx,
      }}
    >
      <FormControlLabel
        control={size === "small" ? <MaterialUISwitch /> : <Android12Switch />}
        checked={checked}
        disabled={disabled}
        label={size === "small" ? "" : label}
        onChange={(e) => {
          onChange(e);
        }}
        sx={{ userSelect: "none" }}
      />
    </FormGroup>
  );
};

SwitchCustom.defaultProps = {
  disabled: false,
  onChange: () => {},
  checked: false,
};

export default SwitchCustom;
